import { Box, Button, Input, InputImperativeActions, Notification, Text } from '@wix/design-system'
import { useContent, useExperiments, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useRef, useState } from 'react'

import { isDomainSupportVoucher } from '../../../api/isDomainSupportVoucher'
import { usePageLogger } from '../../../bi/mfi'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { TWixMobileDomain as Content } from './WixMobileDomain.content'
import { wixMobileDomainDataHooks as dataHooks } from './WixMobileDomain.dataHooks'
import s from './WixMobileDomain.scss'

export const WixMobileDomain: FC = () => {
  const { content } = useContent<Content>()
  const { suggestedDomain } = useStorageContext()
  const [isDomainVoucherSupported, setIsDomainVoucherSupported] = useState(false)
  const [domain, setDomain] = useState(suggestedDomain || '')
  const [pageLoading] = usePageLoading()
  const pageLogger = usePageLogger()
  const next = useNext()
  const experiments = useExperiments()
  const isMobileInputEnterKeyHint = experiments['specs.funnel.MobileInputEnterKeyHint'] === 'true'
  const inputRef = useRef<InputImperativeActions>(null)

  const isShowDomainBanner = content.domainBanner && isDomainVoucherSupported

  const getDomainVoucherSupported = async () => {
    if (!suggestedDomain) {
      return
    }
    try {
      const domainsResponse = await isDomainSupportVoucher(domain)
      setIsDomainVoucherSupported(domainsResponse.data.isVoucherSupported)
      void pageLogger.generalAction({ action: JSON.stringify(domainsResponse.data) })
    } catch (err) {
      console.error('GET_VOUCHER_ERR', err)
    }
  }

  const onBackButtonClick = () => {
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }

  const onNextButtonClick = async () => {
    if (pageLoading) {
      return
    }

    await pageLogger.answer({ answer: domain })
    next({ [content.question.questionKey]: { values: [domain] } })
  }

  const onSkipButtonClick = () => {
    if (pageLoading) {
      return
    }
    void pageLogger.skip()
    next({ [content.question.questionKey]: { skipped: true } })
  }

  useEffect(() => {
    void pageLogger.screenView({ initialData: suggestedDomain })

    if (content.domainBannerControl === 'isVoucherSupported') {
      getDomainVoucherSupported()
    } else if (content.domainBannerControl === 'alwaysShow') {
      setIsDomainVoucherSupported(true)
    }
  }, [pageLogger])

  useEffect(() => {
    if (isMobileInputEnterKeyHint) {
      inputRef.current?.input?.setAttribute('enterkeyhint', 'search')
    }
  }, [isMobileInputEnterKeyHint])

  return (
    <WixMobilePage>
      <WixMobileHeader
        leftContent={<WixMobileBackButton onBack={onBackButtonClick} dataHook={dataHooks.back()} />}
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={dataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      {content.successMessage && (
        <Notification theme="success" dataHook={dataHooks.successMessage()} type="local" show zIndex={0}>
          <Notification.TextLabel>{content.successMessage}</Notification.TextLabel>
        </Notification>
      )}
      <WixMobileContentWrapper className={s.container}>
        <WixMobileHeading dataHook={dataHooks.title()}>{content.question.content.title}</WixMobileHeading>
        <WixMobileDescription dataHook={dataHooks.subtitle()}>
          <span id="description">{content.question.content.subtitle}</span>
        </WixMobileDescription>
        <Input
          ref={inputRef}
          value={domain}
          onChange={e => {
            if (content.domainBannerControl === 'isVoucherSupported') {
              setIsDomainVoucherSupported(false)
            }
            setDomain(e.target.value)
          }}
          placeholder={content.question.content.placeholder}
          dataHook={dataHooks.input()}
          clearButton
          onClear={() => setDomain('')}
          size="large"
          border="round"
        />

        {isShowDomainBanner && (
          <Box
            border="1px solid #87CEA5"
            background="#E1F4EB"
            padding="12px"
            borderRadius="6px"
            marginTop="20px"
            gap="8px"
            verticalAlign="top"
          >
            <PresentIcon />
            <Text dataHook={dataHooks.domainBanner()}>{content.domainBanner}</Text>
          </Box>
        )}
      </WixMobileContentWrapper>
      <WixMobileFooter>
        {content.next && (
          <Button
            size="large"
            fullWidth
            onClick={onNextButtonClick}
            dataHook={dataHooks.next()}
            disabled={domain === ''}
          >
            <Box gap="SP1" verticalAlign="middle">
              {content.next}
            </Box>
          </Button>
        )}
      </WixMobileFooter>
    </WixMobilePage>
  )
}

const PresentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9 8C7.89543 8 7 7.10457 7 6C7 4.89543 7.89543 4 9 4C10.1516 4 10.985 4.63661 11.5 5.90983C12.015 4.63661 12.8484 4 14 4C15.1046 4 16 4.89543 16 6C16 7.10457 15.1046 8 14 8H18C18.5523 8 19 8.44772 19 9V12H18V18C18 18.5523 17.5523 19 17 19H6C5.44772 19 5 18.5523 5 18V12H4V9C4 8.44772 4.44772 8 5 8H9ZM12 9V11H18V9H12ZM11 9H5V11H11V9ZM11 12H6V18H11V12ZM12 12V18H17V12H12ZM14 7C14.5523 7 15 6.55228 15 6C15 5.44772 14.5523 5 14 5C13.1439 5 12.5392 5.59035 12.1926 7H14ZM9 7H10.8074C10.4608 5.59035 9.85605 5 9 5C8.44772 5 8 5.44772 8 6C8 6.55228 8.44772 7 9 7Z"
      fill="#000624"
    />
  </svg>
)
